import * as React from 'react';
import {
	Box,
	Card,
	CardContent,
	TextField,
	Button,
	Typography,
	Container,
	Alert,
	Checkbox,
	FormControlLabel,
	Link,
} from '@mui/material';
import { useNavigate, useLocation } from "react-router-dom";
import EventsApi from "../../api/eventsApi/eventsApi";
import ApiBase from "../../api/apiBase";
import { jwtDecode } from "jwt-decode";
import CacheService from '../../services/CacheService';
import { AppDispatch } from "../../redux/store";
import { login } from "../../redux/actions/authActions";

interface CustomJwtPayload {
	exp: number;
	iss: string;
	nbf: number;
	'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier': string;
	'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress': string;
	'http://schemas.microsoft.com/ws/2008/06/identity/claims/role': string;
	permissions: string[];
}

export default function TokenVerification() {
	const dispatch = AppDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const email = location.state?.email || '';
	
	const [verificationCode, setVerificationCode] = React.useState('');
	const [aydinlatmaChecked, setAydinlatmaChecked] = React.useState(false);
	const [kvkkChecked, setKvkkChecked] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);
	const [success, setSuccess] = React.useState<string | null>(null);
	const [loading, setLoading] = React.useState(false);
	const [sendingCode, setSendingCode] = React.useState(false);


	// Email yoksa login sayfasına yönlendir ve otomatik olarak email token gönder
	React.useEffect(() => {
		if (!email) {
			navigate('/login');
		} else {
			// Sayfa yüklendiğinde otomatik olarak email token gönder
			const sendEmailToken = async () => {
				setError(null);
				setSendingCode(true);
				
				try {
					await EventsApi.sendEmailValidation({ email });
					setSuccess('📧 Doğrulama kodu e-posta adresinize gönderildi.');
				} catch (error: any) {
					console.error("Send code error:", error);
					setError("Kod gönderilirken bir hata oluştu. Lütfen tekrar deneyin.");
				} finally {
					setSendingCode(false);
				}
			};
			
			sendEmailToken();
		}
	}, [email, navigate]);

	const handleSendCode = async () => {
		setError(null);
		setSendingCode(true);
		
		try {
			await EventsApi.sendEmailValidation({ email });
			setSuccess('📧 Doğrulama kodu e-posta adresinize gönderildi.');
		} catch (error: any) {
			console.error("Send code error:", error);
			setError("Kod gönderilirken bir hata oluştu. Lütfen tekrar deneyin.");
		} finally {
			setSendingCode(false);
		}
	};

	const handleVerification = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(null);
		setLoading(true);

		// Kontroller
		if (!verificationCode || verificationCode.length !== 6) {
			setError("Lütfen 6 haneli doğrulama kodunu giriniz.");
			setLoading(false);
			return;
		}

		if (!aydinlatmaChecked || !kvkkChecked) {
			setError("Lütfen tüm koşulları kabul ediniz.");
			setLoading(false);
			return;
		}

		try {
			// Önce kodu doğrula
			const verifyResponse = await EventsApi.verifyEmailCode({
				email,
				code: verificationCode,
				validationType: 1
			});

			if (!verifyResponse.isSuccess || !verifyResponse.isVerified) {
				throw new Error(verifyResponse.message || "Kod doğrulama başarısız");
			}

			// Kod doğrulandıktan sonra tekrar login yap
			const credentials = { email, password: location.state?.password };
			
			if (!credentials.password) {
				setError("Bir hata oluştu. Lütfen tekrar giriş yapmayı deneyin.");
				setTimeout(() => navigate('/login'), 2000);
				return;
			}

			const loginResponse = await ApiBase.login(credentials);
			const token = loginResponse.accessToken?.token || loginResponse.token;
			const permissions = loginResponse.permissions || [];

			if (!token || typeof token !== "string") {
				throw new Error("Invalid token: Token is missing or not a string");
			}

			// Decode token to get userId
			const decodedToken = jwtDecode<CustomJwtPayload>(token);
			const userId = decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
			
			// Önce tüm önbelleği temizle
			CacheService.clearAll();
			
			// Save token and userId to localStorage and CacheService
			localStorage.setItem("authToken", token);
			localStorage.setItem("userId", userId);
			CacheService.setAuthToken(token);
			
			// Set authorization header
			ApiBase.putAccessTokenInfo(token);

			dispatch(
				login({
					accessToken: token,
					permissions,
				})
			);

			console.log('✅ Email Verification: Auth setup completed, navigating to home...');
			
			setTimeout(() => {
				navigate("/");
			}, 50);

		} catch (error: any) {
			console.error("Verification Error:", error);
			setError("Doğrulama başarısız. Lütfen kodu kontrol edin.");
		} finally {
			setLoading(false);
		}
	};



	return (
		<Box
			sx={{
				minHeight: '100vh',
				backgroundColor: '#ffffff',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: 2,
			}}
		>
			<Container maxWidth="sm">
				<Card
					elevation={3}
					sx={{
						borderRadius: 3,
						backgroundColor: '#ffffff',
						border: '1px solid #e0e0e0',
					}}
				>
					<CardContent sx={{ p: 4 }}>
						<Box component="form" onSubmit={handleVerification} noValidate>
							<Typography
								variant="h5"
								component="h2"
								gutterBottom
								sx={{
									fontWeight: 600,
									color: '#094151',
									mb: 2,
								}}
							>
								E-posta Doğrulama
							</Typography>

							<Typography
								variant="body2"
								sx={{ mb: 3, color: '#666' }}
							>
								{email} adresine gönderilen 6 haneli doğrulama kodunu giriniz.
							</Typography>

							{error && (
								<Alert severity="error" sx={{ mb: 3 }}>
									{error}
								</Alert>
							)}

							{success && (
								<Alert severity="success" sx={{ mb: 3 }}>
									{success}
								</Alert>
							)}

							<Box mb={3}>
								<Typography
									variant="subtitle2"
									sx={{ mb: 1, fontWeight: 600, color: '#333' }}
								>
									Doğrulama Kodu
								</Typography>
								<Box display="flex" gap={1}>
									<TextField
										required
										fullWidth
										value={verificationCode}
										onChange={(e) => {
											const value = e.target.value.replace(/\D/g, '').slice(0, 6);
											setVerificationCode(value);
										}}
										placeholder="123456"
										variant="outlined"
										inputProps={{
											maxLength: 6,
											style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.5rem' }
										}}
										sx={{
											'& .MuiOutlinedInput-root': {
												borderRadius: 2,
												backgroundColor: '#f8f9ff',
												'&:hover fieldset': {
													borderColor: '#094151',
												},
												'&.Mui-focused fieldset': {
													borderColor: '#094151',
												},
											},
										}}
									/>
									<Button
										variant="contained"
										onClick={handleSendCode}
										disabled={sendingCode}
										sx={{
											minWidth: '120px',
											backgroundColor: '#fff',
											color: '#094151',
											border: '1.5px solid #094151',
											fontWeight: 600,
											borderRadius: '8px',
											boxShadow: 'none',
											'&:hover': {
												backgroundColor: '#d0e7ec',
												color: '#094151',
											},
											'&:disabled': {
												backgroundColor: '#f5f5f5',
												color: '#999',
												border: '1.5px solid #ccc',
											},
										}}
									>
										{sendingCode ? 'Gönderiliyor...' : 'Kod Gönder'}
									</Button>
								</Box>
							</Box>

							<Box mb={3}>
								<FormControlLabel
									control={
										<Checkbox
											checked={aydinlatmaChecked}
											onChange={(e) => setAydinlatmaChecked(e.target.checked)}
											sx={{
												color: '#094151',
												'&.Mui-checked': {
													color: '#094151',
												},
											}}
										/>
									}
									label={
										<Typography variant="body2">
											<Link
												href="/UMAY PLATFORM - TERMS OF USE & DISCLAIMER.pdf"
												target="_blank"
												rel="noopener noreferrer"
												sx={{
													color: '#094151',
													textDecoration: 'underline',
													fontWeight: 500,
												}}
											>
												Aydınlatma Metni
											</Link>
											'ni okudum ve anladım.
										</Typography>
									}
								/>
							</Box>

							<Box mb={4}>
								<FormControlLabel
									control={
										<Checkbox
											checked={kvkkChecked}
											onChange={(e) => setKvkkChecked(e.target.checked)}
											sx={{
												color: '#094151',
												'&.Mui-checked': {
													color: '#094151',
												},
											}}
										/>
									}
									label={
										<Typography variant="body2">
											<Link
												href="/UMAY PLATFORMU KVKK AYDINLATMA METNİ.pdf"
												target="_blank"
												rel="noopener noreferrer"
												sx={{
													color: '#094151',
													textDecoration: 'underline',
													fontWeight: 500,
												}}
											>
												KVKK Metni
											</Link>
											'ni okudum ve anladım.
										</Typography>
									}
								/>
							</Box>

							<Button
								type="submit"
								fullWidth
								variant="contained"
								disabled={loading || !verificationCode || verificationCode.length !== 6 || !aydinlatmaChecked || !kvkkChecked}
								sx={{
									mt: 2,
									mb: 3,
									py: 1.5,
									backgroundColor: '#094151',
									color: '#fff',
									fontWeight: 600,
									borderRadius: '8px',
									boxShadow: 'none',
									'&:hover': {
										backgroundColor: '#022631',
										color: '#fff',
									},
									'&:disabled': {
										backgroundColor: '#ccc',
									},
								}}
							>
								{loading ? 'Doğrulanıyor...' : 'Giriş Yap'}
							</Button>

							<Box textAlign="center">
								<Link
									component="button"
									type="button"
									variant="body2"
									sx={{
										color: '#094151',
										textDecoration: 'none',
										fontWeight: 500,
										'&:hover': {
											textDecoration: 'underline',
										},
									}}
									onClick={(e) => {
										e.preventDefault();
										navigate('/login');
									}}
								>
									← Giriş sayfasına dön
								</Link>
							</Box>
						</Box>
					</CardContent>
				</Card>

				{/* Logo and Company Name */}
				<Box 
					sx={{ 
						display: 'flex', 
						alignItems: 'center', 
						justifyContent: 'center',
						gap: 1,
						mt: 3,
						opacity: 0.8
					}}
				>
					<img
						src="/logo_inttech.png"
						alt="Int-Tech Logo"
						style={{
							width: '20px',
							height: '20px',
							objectFit: 'contain',
						}}
					/>
					<Typography
						variant="body2"
						component="div"
						sx={{
							fontFamily: 'Conthrax, monospace',
							fontWeight: 600,
							letterSpacing: '.08rem',
							color: '#666',
							fontSize: '0.85rem',
						}}
					>
						INT-TECH
					</Typography>
				</Box>
			</Container>


		</Box>
	);
} 